import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone } from '@fortawesome/sharp-solid-svg-icons';
import { type FC, useCallback, useEffect, useState } from 'react';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { useAuth } from '@/providers/auth-provider';
import { useToastContext } from '@/providers/ToastProvider';
import { bindLoadingStateToCallback } from '@/utils/bindLoadingStateToCallback';
import { copyToClipboard } from '@/utils/copyToClipboard';

export const SelectionCopyableLinks: FC = () => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const { userCanShareSelections } = useAuth();
  const {
    getEmbedLink,
    canCopyEmbedLink,
    getDeepLinkShareUrl,
    canCopyDeepLinkShareUrl,
    totalSelectionCount,
  } = useSelectionsPanelContext();

  const [embedLink, setEmbedLink] = useState<string | null | undefined>(null);
  const [deepLinkShareUrl, setDeepLinkShareUrl] = useState<
    string | null | undefined
  >(null);
  const [isEmbedLinkLoading, setIsEmbedLinkLoading] = useState<boolean>(false);
  const [isDeepLinkShareUrlLoading, setIsDeepLinkShareUrlLoading] =
    useState<boolean>(false);

  // Bust the cache when the maker function rebuilds
  useEffect(() => {
    setEmbedLink(null);
  }, [getEmbedLink]);

  // Bust the cache when the maker function rebuilds
  useEffect(() => {
    setDeepLinkShareUrl(null);
  }, [getDeepLinkShareUrl]);

  const buildAndCopyEmbedCode = useCallback(async () => {
    await bindLoadingStateToCallback(async () => {
      if (!userCanShareSelections || !canCopyEmbedLink) return;
      let link = embedLink;
      if (!link) {
        link = await getEmbedLink({ showCustomizations: true });
        setEmbedLink(link);
      }
      if (!link) {
        showErrorToast('Failed to get Embed Link');
        return;
      }
      await copyToClipboard(link);
      showSuccessToast('Successfully Copied');
    }, setIsEmbedLinkLoading);
  }, [
    canCopyEmbedLink,
    embedLink,
    getEmbedLink,
    showErrorToast,
    showSuccessToast,
    userCanShareSelections,
  ]);

  const buildAndCopyDeepLinkShareUrl = useCallback(async () => {
    await bindLoadingStateToCallback(async () => {
      if (!userCanShareSelections || !canCopyDeepLinkShareUrl) return;
      let url = deepLinkShareUrl;
      if (!url) {
        url = await getDeepLinkShareUrl();
        setDeepLinkShareUrl(url);
      }
      if (!url) {
        showErrorToast('Failed to get Selections Link');
        return;
      }
      await copyToClipboard(url);
      showSuccessToast('Successfully Copied');
    }, setIsDeepLinkShareUrlLoading);
  }, [
    canCopyDeepLinkShareUrl,
    deepLinkShareUrl,
    getDeepLinkShareUrl,
    showErrorToast,
    showSuccessToast,
    userCanShareSelections,
  ]);

  return (
    <div className="flex flex-col gap-4 px-4 pb-3 pt-4">
      <Typography className="pb-1" variant="button2">
        Share
      </Typography>
      <div
        className="w-full"
        data-tooltip-content={!totalSelectionCount ? 'Make a Selection' : ''}
        data-tooltip-delay-show={300}
        data-tooltip-id={GLOBAL_TOOLTIP}
      >
        <Button
          className="h-8 w-full px-3 py-2"
          disabled={
            !userCanShareSelections || !canCopyEmbedLink || !totalSelectionCount
          }
          isLoading={isEmbedLinkLoading}
          size={ButtonSize.SMALL}
          onClick={buildAndCopyEmbedCode}
        >
          <Typography variant="button2">
            <FontAwesomeIcon className="mr-2" icon={faClone} />
            Copy Map Link
          </Typography>
        </Button>
      </div>
      <div
        className="w-full"
        data-tooltip-content={!totalSelectionCount ? 'Make a Selection' : ''}
        data-tooltip-delay-show={300}
        data-tooltip-id={GLOBAL_TOOLTIP}
      >
        <Button
          className="h-8 w-full px-3 py-2"
          disabled={
            !userCanShareSelections ||
            !canCopyDeepLinkShareUrl ||
            !totalSelectionCount
          }
          isLoading={isDeepLinkShareUrlLoading}
          size={ButtonSize.SMALL}
          variant={ButtonVariant.TERTIARY}
          onClick={buildAndCopyDeepLinkShareUrl}
        >
          <FontAwesomeIcon className="mr-2" icon={faClone} />
          <Typography variant="button2">Copy Selections Link</Typography>
        </Button>
      </div>
    </div>
  );
};
